import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Archive as ArchiveIcon, RotateCcw, Trash2, ShieldAlert } from "lucide-react";
import { formatDateTime, displayAcCode } from "../lib/format";
import { toast } from "sonner";

function ArchiveTab({ entity }) {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteFor, setDeleteFor] = useState(null);
    const [password, setPassword] = useState("");

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/archive", { params: { entity } });
            setList(data);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [entity]);

    async function restore(item) {
        try {
            let path;
            if (entity === "pigmy") path = `/pigmy/accounts/${item.id}/restore`;
            else if (entity === "investor") path = `/investors/${item.id}/restore`;
            else path = `/${entity}s/${item.id}/restore`;
            await api.post(path);
            toast.success("Restored");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function permanentDelete() {
        try {
            let path;
            if (entity === "pigmy") path = `/pigmy/accounts/${deleteFor.id}/permanent`;
            else if (entity === "investor") path = `/investors/${deleteFor.id}/permanent`;
            else path = `/${entity}s/${deleteFor.id}/permanent`;
            await api.delete(path, { data: { password } });
            toast.success("Permanently deleted");
            setDeleteFor(null);
            setPassword("");
            load();
        } catch (e) {
            toast.error(formatApiError(e) || "Wrong admin password");
        }
    }

    return (
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
            {loading ? (
                <div className="p-8 text-sm text-slate-400 text-center">Loading…</div>
            ) : list.length === 0 ? (
                <div className="p-8 text-sm text-slate-400 text-center">
                    No archived {entity}s.
                </div>
            ) : (
                <div className="divide-y divide-slate-100">
                    {list.map((item) => (
                        <div
                            key={item.id}
                            className="px-4 py-3 flex items-center justify-between flex-wrap gap-3"
                            data-testid={`archive-row-${item.id}`}
                        >
                            <div className="min-w-0 flex-1">
                                {entity === "customer" ? (
                                    <Link
                                        to={`/customers/${item.id}`}
                                        className="font-semibold text-slate-900 hover:underline truncate"
                                    >
                                        {item.name}{" "}
                                        <span className="text-xs font-mono text-slate-500">
                                            {item.customer_code}
                                        </span>
                                    </Link>
                                ) : entity === "loan" ? (
                                    <Link
                                        to={`/loans/${item.id}`}
                                        className="font-semibold text-slate-900 hover:underline truncate"
                                    >
                                        Loan {item.loan_code} · ₹{item.principal}
                                    </Link>
                                ) : entity === "pigmy" ? (
                                    <Link
                                        to={`/pigmy/${item.id}`}
                                        className="font-semibold text-slate-900 hover:underline truncate"
                                    >
                                        Saving Scheme {displayAcCode(item.account_code)} · {item.customer_name || item.customer_code || "—"}
                                    </Link>
                                ) : (
                                    <Link
                                        to={`/investors/${item.id}`}
                                        className="font-semibold text-slate-900 hover:underline truncate"
                                    >
                                        Investor {item.investor_code} · {item.name} · ₹{item.principal}
                                    </Link>
                                )}
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Archived {formatDateTime(item.archived_at)} by{" "}
                                    {item.archived_by || "—"}
                                    {item.archive_reason && (
                                        <span className="italic"> · "{item.archive_reason}"</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-1.5 shrink-0">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => restore(item)}
                                    data-testid={`restore-${item.id}`}
                                    className="rounded-lg"
                                >
                                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => setDeleteFor(item)}
                                    data-testid={`perm-delete-${item.id}`}
                                    className="rounded-lg bg-rose-600 hover:bg-rose-700"
                                >
                                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Forever
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!deleteFor} onOpenChange={(o) => { if (!o) { setDeleteFor(null); setPassword(""); } }}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-rose-700">
                            <ShieldAlert className="h-5 w-5" /> Permanent delete confirmation
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-slate-700">
                            This will <b>permanently delete</b> all data for{" "}
                            <b>{deleteFor?.name || deleteFor?.loan_code}</b>, including loans,
                            payments and collections. <b className="text-rose-700">This cannot be undone.</b>
                        </p>
                        <div>
                            <Label>Enter admin password to confirm</Label>
                            <Input
                                type="password"
                                data-testid="perm-delete-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="h-11 rounded-xl mt-1.5"
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setDeleteFor(null); setPassword(""); }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={!password}
                            onClick={permanentDelete}
                            data-testid="perm-delete-confirm"
                            className="bg-rose-600 hover:bg-rose-700"
                        >
                            Permanently Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

export default function ArchivePage() {
    return (
        <div className="space-y-5">
            <div className="flex items-center gap-2">
                <ArchiveIcon className="h-5 w-5 text-slate-500" />
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                    Archive
                </h1>
            </div>
            <p className="text-sm text-slate-500 -mt-3">
                Records hidden from the main app. View, restore, or permanently delete
                with admin-password confirmation. Permanent deletion cannot be undone.
            </p>
            <Tabs defaultValue="customer">
                <TabsList className="bg-white border border-slate-100 rounded-xl">
                    <TabsTrigger value="customer" data-testid="archive-tab-customer">Customers</TabsTrigger>
                    <TabsTrigger value="loan" data-testid="archive-tab-loan">Loans</TabsTrigger>
                    <TabsTrigger value="pigmy" data-testid="archive-tab-pigmy">Saving Scheme</TabsTrigger>
                    <TabsTrigger value="investor" data-testid="archive-tab-investor">Investors</TabsTrigger>
                </TabsList>
                <TabsContent value="customer" className="mt-4">
                    <ArchiveTab entity="customer" />
                </TabsContent>
                <TabsContent value="loan" className="mt-4">
                    <ArchiveTab entity="loan" />
                </TabsContent>
                <TabsContent value="pigmy" className="mt-4">
                    <ArchiveTab entity="pigmy" />
                </TabsContent>
                <TabsContent value="investor" className="mt-4">
                    <ArchiveTab entity="investor" />
                </TabsContent>
            </Tabs>
        </div>
    );
}
